import cx from 'classnames';
import Image from 'next/image';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { getAmplitudeItineraryPrice } from '../../../../../../design-system/components/card/itinerary/ItineraryCard';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { Ratio, RATIO } from '../../../../../../design-system/components/ratio/Ratio';
import { imageLoaderForPicture } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { getAmplitudeRecommendedItineraryTags } from '../../../../../../resources/itinerary/domain/operations/getAmplitudeTags';
import { Itinerary } from '../../../../../../resources/itinerary/domain/types/Itinerary';
import { WishListButton } from '../../../../../../shared/components/WishListButton/WishListButton';
import { useLocale } from '../../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { useCurrency } from '../../../../../../shared/providers/currency/useCurrency';
import { useToggleWishList } from '../../../../../../shared/providers/wishList/useToggleWishList';
import { useWishList } from '../../../../../../shared/providers/wishList/useWishList';
import { roundedPriceWithCurrencySymbol } from '../../../../../../shared/utilities/priceFormat/priceFormat';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import { ItineraryTagList } from './ItineraryTagList';

import style from './RecommendedItinerary.module.css';

type RecommendedItineraryProps = {
    recommendedItinerary: PickDomain<
        Itinerary,
        | 'id'
        | 'slug'
        | 'activityTypes'
        | 'picture'
        | 'duration'
        | 'title'
        | 'startingPrice'
        | 'isGIR'
        | 'highlights'
    > & {
        typeOfProfile: Itinerary['typeOfProfile'] | null;
        destination: PickDomain<Itinerary['destination'], 'slug'>;
    };
    agency: PickDomain<Agency, 'id' | 'slug' | 'betterTripIndex'>;
    destination: PickDomain<Destination, 'id' | 'slug'>;
};

export const RecommendedItinerary = (props: RecommendedItineraryProps) => {
    const { recommendedItinerary, agency, destination } = props;
    const { wishList, addItinerary, removeItinerary } = useWishList();
    const { formatMessage } = useIntl();
    const { currentCurrency } = useCurrency();
    const { locale } = useLocale();
    const { trackEvent } = useTrackEventAmplitude();
    const { generatePath } = useUrlGenerator();

    const { toggleWishList } = useToggleWishList(
        recommendedItinerary.id,
        useCallback(
            (itineraryId) => {
                addItinerary(itineraryId);

                trackEvent((ampli, defaultProperties) => {
                    ampli.itineraryWishlistAdded({
                        ...defaultProperties,
                        itinerary_id: recommendedItinerary.id,
                        itinerary_slug: recommendedItinerary.slug,
                        block: 'highlighted_itinerary',
                    });
                });
            },
            [addItinerary, recommendedItinerary.id, recommendedItinerary.slug, trackEvent],
        ),
        useCallback(
            (itineraryId) => {
                removeItinerary(itineraryId);
            },
            [removeItinerary],
        ),
    );

    const isFamily = recommendedItinerary.typeOfProfile?.type === 'family';
    const { isGIR } = recommendedItinerary;

    const familyTag = formatMessage({ id: 'page.itinerariesList.filters.family' });
    const girTag = formatMessage({ id: 'global.component.itineraryCard.GIR' });

    const tags = recommendedItinerary.activityTypes.slice(0, 3).reduce<string[]>((acc, curr) => {
        if (isFamily && !acc.includes(familyTag)) return [familyTag, ...acc, curr];
        if (isGIR && !acc.includes(girTag)) return [girTag, ...acc, curr];
        return [...acc, curr];
    }, []);

    const handleItineraryClicked = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.itineraryClicked({
                ...defaultProperties,
                itinerary_id: recommendedItinerary.id,
                itinerary_slug: recommendedItinerary.slug,
                block: 'highlighted_itinerary',
                agency_better_trip_index: agency.betterTripIndex,
                agency_id: agency.id,
                agency_slug: agency.slug,
                duration: recommendedItinerary.duration.toString(),
                price: getAmplitudeItineraryPrice(
                    recommendedItinerary.startingPrice,
                    currentCurrency,
                ),
                tags: getAmplitudeRecommendedItineraryTags(recommendedItinerary),
                row_number: 1, // @todo: à supprimer côté Amplitude, toujours à 0 ou 1...
                card_number: 1,
                destination_id: destination.id,
                destination_slug: destination.slug,
                tool_counter: 0,
                is_refund_eligible: false,
            });
        });
    };

    return (
        <section className={cx('main-container', style.recommendedSection)}>
            <div className={style.wrapper}>
                <div className={style.imageWrapper}>
                    <Ratio ratio={RATIO['4/3']}>
                        <Image
                            src={recommendedItinerary.picture.id}
                            alt={recommendedItinerary.picture.author}
                            loader={imageLoaderForPicture(recommendedItinerary.picture, {
                                aspectRatio: RATIO['4/3'],
                            })}
                            className={style.picture}
                            width={738}
                            height={498}
                        />
                    </Ratio>
                    <WishListButton
                        onToggle={toggleWishList}
                        isWishListed={wishList.itineraries.includes(recommendedItinerary.id)}
                        className={style.wishListButton}
                    />
                </div>

                <div className={style.contentWrapper}>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <div className={cx(style.subtitle, 'font-body-bold')}>
                        {formatMessage({ id: 'page.agency.highlightFromAgency' })}
                    </div>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <h2 className={cx('font-secondary-m-bold', style.title)}>
                        {recommendedItinerary.title}
                    </h2>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <p className="font-body-bold">
                        {formatMessage(
                            { id: 'global.component.itineraryCard.durationWithPrice' },
                            {
                                duration: recommendedItinerary.duration,
                                price: `${roundedPriceWithCurrencySymbol({
                                    currency: currentCurrency,
                                    locale,
                                    price: recommendedItinerary.startingPrice,
                                })}`,
                            },
                        )}
                    </p>
                    <ItineraryTagList tags={tags} />

                    {recommendedItinerary.highlights
                        ? recommendedItinerary.highlights.map((highlight, index) => (
                              <li key={index} className={style.highlight}>
                                  {highlight}
                              </li>
                          ))
                        : null}

                    <Button
                        priority="secondary"
                        className={style.cta}
                        onClick={handleItineraryClicked}
                        href={generatePath('itinerary', {
                            destinationSlug: recommendedItinerary.destination.slug,
                            itineraryId: recommendedItinerary.id,
                            itinerarySlug: recommendedItinerary.slug,
                        })}
                    >
                        {formatMessage({ id: 'page.agency.seeCompleteItinerary' })}
                    </Button>
                </div>
            </div>
        </section>
    );
};
